// Real HTTP adapter for FEATURE_050 (Log Inquiry) — talks to admin-api at
// GET /api/v1/admin/audit-logs. Numbered pages (page/size) with server-side
// sorting (sort/dir). Activated by setting VITE_USE_MOCK_API=false; see log.adapter.ts.
// Maps the server error envelope { error: { code, message, fields, meta } } to
// LogInquiryApiError.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  LogInquiryApiError,
  type ExportLogsResult,
  type ListLogsParams,
  type ListLogsResult,
  type LogInquiryApi,
  type LogInquiryApiErrorCode,
} from '../types/log.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  const payload = candidate.response?.data?.error;
  throw new LogInquiryApiError(
    (payload?.code as LogInquiryApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields, meta: payload?.meta },
  );
}

export const logInquiryHttpApi: LogInquiryApi = {
  // GET /admin/audit-logs?dateFrom&dateTo&source&category&userType&status&search
  //   &page&size&sort&dir.
  // `page` is zero-based; the response echoes it back along with totalElements /
  // totalPages, which is what lets the screen draw its numbered pagination bar.
  async listLogs(params: ListLogsParams): Promise<ListLogsResult> {
    try {
      const res = await httpClient.get<ApiEnvelope<ListLogsResult>>(
        endpoints.logs,
        { params },
      );
      const data = res.data?.data;
      // The table iterates `items` and the pagination bar reads `totalPages`; a
      // non-object body, a non-array `items` or a missing page count (wrong
      // envelope, HTML, bare array) must surface as a load error rather than
      // white-screen the table.
      if (
        typeof data !== 'object' ||
        data === null ||
        !Array.isArray(data.items) ||
        typeof data.totalPages !== 'number'
      ) {
        throw new LogInquiryApiError('UNKNOWN', '로그를 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  // GET /admin/audit-logs/export?<same filters as the list>. The BE streams a
  // ready-made CSV (server-rendered Korean labels + subject-PII masking); the FE
  // only downloads the bytes. responseType:'blob' means an ERROR body is ALSO a
  // Blob, so the error path reads + parses it before mapping (see mapBlobError).
  async exportLogs(params: ListLogsParams): Promise<ExportLogsResult> {
    try {
      const res = await httpClient.get<Blob>(endpoints.logsExport, {
        params,
        responseType: 'blob',
      });
      const disposition = (res.headers['content-disposition'] as string | undefined) ?? '';
      return { blob: res.data, filename: parseFilename(disposition) };
    } catch (e) {
      return await mapBlobError(e);
    }
  },
};

// Pull `filename="…"` out of a Content-Disposition header; fall back to a stable
// default so a missing/odd header never breaks the download.
function parseFilename(contentDisposition: string): string {
  const match = /filename="?([^"]+)"?/i.exec(contentDisposition);
  return match?.[1] ?? 'audit-logs.csv';
}

// Error variant of mapError for a blob-typed request: the failed response body
// is a Blob, so read its text and parse the standard { error: {...} } envelope.
async function mapBlobError(e: unknown): Promise<never> {
  const candidate = e as { response?: { data?: unknown } };
  const data = candidate.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as ServerErrorEnvelope;
      const payload = parsed.error;
      throw new LogInquiryApiError(
        (payload?.code as LogInquiryApiErrorCode) ?? 'UNKNOWN',
        payload?.message ?? '알 수 없는 오류가 발생했습니다.',
        { fields: payload?.fields, meta: payload?.meta },
      );
    } catch (parseErr) {
      if (parseErr instanceof LogInquiryApiError) throw parseErr;
      // fall through to the generic mapping below
    }
  }
  mapError(e);
}
